import React from 'react';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, Award, Users, Box, ExternalLink, Copy, Check, Filter, ChevronRight, Star, MapPin } from 'lucide-react';
// Three fields per competitor, never the comparison pages themselves — the
// footer ships in every bundle on the site, and ./index would put 100 KB of
// comparison prose in all of them. See src/content/compare/list.js.
import { COMPARISON_LIST } from '../../content/compare/list';
const Footer = () => {
  return (
    <footer className="py-8 md:py-10 bg-[#101512] text-gray-300 text-center mt-4">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 px-6 md:px-0">
        <div className="flex items-center gap-2">
           <img 
            src="/logo.png" 
            alt="MintLink Logo" 
            className="h-7 w-7 md:h-8 md:w-8 object-contain" 
          />
          <div className="text-left">
            <p className="text-sm font-semibold">MintLink</p>
            <p className="text-[11px] text-gray-400">
              Your work deserves more than likes.
            </p>
          </div>
        </div>
        
        <div className="flex gap-4 text-sm flex-wrap justify-center">
          <Link to="/review-my-work" className="hover:text-white transition-colors">
            Review My Work
          </Link>
          <Link to="/tickets" className="hover:text-white transition-colors">
            Tickets
          </Link>
          <Link to="/privacy-policy" className="hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            Terms
          </Link>
          <Link to="/data-deletion" className="hover:text-white transition-colors">
            Data Deletion
          </Link>
          {/* Grievance Officer details must be *displayed* on the site, not just
              documented — a footer link is the conventional placement. */}
          <Link to="/terms#grievance-officer" className="hover:text-white transition-colors">
            Grievance Redressal
          </Link>
          <Link to="/blog" className="hover:text-white transition-colors">
            Blog
          </Link>
          <Link to="/press" className="hover:text-white transition-colors">
            Press
          </Link>
          {/* /marketplace and /creators are the site's two main hub pages, so they
              need a link on every page rather than only from the homepage —
              sitewide internal links with stable, descriptive anchor text are what
              Google reads site structure from when choosing sitelinks. The anchor
              text matches each page's own <h1> and title on purpose. */}
          <Link to="/marketplace" className="hover:text-white transition-colors">
            Marketplace
          </Link>
          <Link to="/creators" className="hover:text-white transition-colors">
            Creators
          </Link>
          <Link to="/how-it-works" className="hover:text-white transition-colors">
            How It Works
          </Link>
          <Link to="/pricing" className="hover:text-white transition-colors">
            Pricing
          </Link>
          <Link to="/faq" className="hover:text-white transition-colors">
            FAQ
          </Link>
          <a href="https://help.mintlink.in/" className="hover:text-white transition-colors">
            Help Centre
          </a>
        </div>
      </div>

      {/* Guides get their own row rather than joining the link soup above.
          These are the pages meant to bring in search traffic, and a sitewide
          link from every page — with the anchor text matching each guide's own
          <h1> — is the strongest internal signal we can give them. The same
          reasoning as the Marketplace/Creators links above, applied to a
          section rather than a page. */}
      <div className="max-w-5xl mx-auto mt-6 px-6 md:px-0">
        <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-2">
          Guides for Indian creators
        </p>
        <div className="flex gap-x-4 gap-y-2 text-[13px] flex-wrap justify-center md:justify-start text-gray-400">
          <Link to="/guides/sell-digital-products-india" className="hover:text-white transition-colors">
            Sell digital products
          </Link>
          <Link to="/guides/sell-canva-templates-india" className="hover:text-white transition-colors">
            Sell Canva templates
          </Link>
          <Link to="/guides/sell-notion-templates-india" className="hover:text-white transition-colors">
            Sell Notion templates
          </Link>
          <Link to="/guides/upi-payments-for-creators" className="hover:text-white transition-colors">
            Accept UPI payments
          </Link>
          <Link to="/guides/gumroad-alternative-india" className="hover:text-white transition-colors">
            Gumroad alternative
          </Link>
          <Link to="/guides/topmate-alternative-india" className="hover:text-white transition-colors">
            Topmate alternative
          </Link>
          <Link to="/guides" className="text-gray-300 hover:text-white transition-colors font-semibold">
            All guides →
          </Link>
        </div>
      </div>

      {/* The ticketing comparisons, on the same reasoning as the guides row
          above: these pages exist to be found in search, and a sitewide link
          with the competitor's name as the anchor text is the strongest
          internal signal we can give them. Anchor text is the bare competitor
          name rather than "MintLink Tickets vs X" because the anchor should
          read as the thing being searched for. */}
      <div className="max-w-5xl mx-auto mt-6 px-6 md:px-0">
        <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-2">
          Compare ticketing platforms
        </p>
        <div className="flex gap-x-4 gap-y-2 text-[13px] flex-wrap justify-center md:justify-start text-gray-400">
          {COMPARISON_LIST.map((c) => (
            <Link
              key={c.slug}
              to={`/tickets/vs/${c.slug}`}
              className="hover:text-white transition-colors"
            >
              {c.competitor} alternative
            </Link>
          ))}
          <Link to="/tickets" className="text-gray-300 hover:text-white transition-colors font-semibold">
            MintLink Tickets →
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-6 flex flex-col items-center gap-2 px-6 md:px-0">
        <p className="text-sm">Made with ❤️ in India</p>

        <p className="text-[11px] text-gray-500">© 2026 MintLink. All rights reserved.</p>
      </div>

      <div className="flex flex-col items-center gap-2 mt-6">
        <p className="text-[11px] text-gray-500 uppercase tracking-wide">Official MintLink Accounts</p>
        <div className="flex items-center gap-3">
          <a
            href="https://www.instagram.com/mintlink.in/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MintLink on Instagram"
            title="MintLink on Instagram"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-[#E1306C] hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
            </svg>
          </a>
          <a
            href="https://www.youtube.com/@MINTLINK-IN4"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MintLink on YouTube"
            title="MintLink on YouTube"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-[#FF0000] hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.386.521A2.994 2.994 0 0 0 .502 6.186 31.336 31.336 0 0 0 0 12a31.336 31.336 0 0 0 .502 5.814 2.994 2.994 0 0 0 2.112 2.12c1.881.521 9.386.521 9.386.521s7.505 0 9.386-.521a2.994 2.994 0 0 0 2.112-2.12A31.336 31.336 0 0 0 24 12a31.336 31.336 0 0 0-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          </a>
          <a
            href="https://www.reddit.com/r/Mintlink/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MintLink on Reddit"
            title="MintLink on Reddit"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-[#FF4500] hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
            </svg>
          </a>
          <a
            href="https://x.com/mintlink_in"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MintLink on X"
            title="MintLink on X"
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        </div>
        <p className="text-[10px] text-gray-600 max-w-md text-center">
          These are our only official accounts — please report any other profile claiming to be MintLink.
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://www.producthunt.com/products/mintlink/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-mintlink"
            target="_blank"
            rel="noopener noreferrer"
            title="Review MintLink on Product Hunt"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=1260537&theme=dark"
              alt="MintLink - Your Work deserves more than Likes | Product Hunt"
              width="200"
              height="43"
              style={{ width: 200, height: 43 }}
            />
          </a>
          {/* The SVG is 296x76 with shadow padding baked in, so at this size
              the visible pill sits level with the Product Hunt badge. */}
          <a
            href="https://kittylaunch.com/p/mintlink?utm_source=badge"
            target="_blank"
            rel="noopener noreferrer"
            title="MintLink on KittyLaunch"
          >
            <img
              src="https://kittylaunch.com/api/public/badges/launch_badge.svg?style=pill&theme=dark"
              alt="MintLink — Verified by KittyLaunch"
              data-kittylaunch-badge="1"
              width="215"
              height="55"
              style={{ width: 215, height: 55 }}
              loading="lazy"
            />
          </a>
        </div>
      </div>

      <p className="text-[10px] text-gray-600 mt-4 px-6 max-w-2xl mx-auto text-center">
        We use Microsoft Clarity to see how you use our website through session recordings and heatmaps. By using our site, you agree that we and Microsoft can collect and use this data.{' '}
        <Link to="/privacy-policy" className="underline hover:text-gray-400 transition-colors">
          Our privacy policy has more details.
        </Link>
      </p>
      <p className="text-[10px] text-gray-600 mt-2 px-6 max-w-2xl mx-auto text-center">
        MintLink is a product of{' '}
        <a
          href="https://tracxn.com/d/legal-entities/india/deltadots-ai-tech-private-limited/__G-bRlAq4CB5_-T9I63wv4I7S72N1AMiLe3VgCVLHYCY#about"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-400 transition-colors"
        >
          Deltadots AI Tech Private Limited
        </a>
      </p>
      <p className="text-[10px] text-gray-600 mt-1 px-6 max-w-2xl mx-auto text-center">
        Binayak Enclave, R-402, 59, Kalicharan Ghosh Road, Sinthee More, Kolkata — 700050, West Bengal, India
      </p>
    </footer>
  );
};

export default Footer;